import {
  DefaultError,
  InfiniteData,
  QueryKey,
  queryOptions,
  UndefinedInitialDataInfiniteOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions
} from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import {
  Assignment,
  EnrichedTeacherClassAssignmentWithContentAssignees,
  FetchExamQuestionAnswersResponse,
  FetchTeacherAssignments,
  TeacherClassAssignment
} from "@seneca/assignments-service-types";
import { ActiveUsers } from "@seneca/school-analytics-service-types";
import { SchoolParentConnectionsInfoResponse } from "@seneca/schools-service-types";
import { removeUndefinedEntries } from "@seneca/small-utils-library";
import { FetchAllTagsResponse } from "@seneca/tags-service-types";
import {
  FetchSchoolQuizOverviewsResponse,
  FetchTeacherQuizOverviewsResponse,
  QuizOverviewI,
  TeacherQuizI
} from "@seneca/user-quiz-service-types";

import { QuestionDifficulty } from "services/shortAnswerService/question-types/common";
import { EQEnrichedCourseSections } from "services/shortAnswerService/question-types/QuestionCourseSections";

import enrichEQWithGenerationSeed from "seneca-common/features/exam-questions/utils/enrichEQWithGenerationSeed";
import {
  QueryOptions,
  SuspenseQueryOptions
} from "seneca-common/utils/reactQuery/types";

import { getTagsQueryOptions } from "features/tags/client";

import {
  fetchActiveUsersBySchool,
  fetchAllTeacherClassAssignments,
  fetchAssignment,
  fetchAssignmentExamQuestionAnswers,
  fetchClassAssignment,
  fetchClassAssignments,
  fetchClassesSectionStats,
  fetchEnrichedAggregatedUserSectionStats,
  fetchFullSchoolClassesTeacher,
  fetchSchoolClassEntitiesTeacher,
  fetchSchoolConnectionInfo,
  fetchStudentQuiz,
  fetchTeacherAssignments,
  fetchTeacherQuiz,
  fetchUserSectionStats,
  queryEnrichedCourseSections,
  querySchoolTeacherQuizOverviews,
  queryTeacherQuizOverviews,
  UserSectionStatsGraphQlArgs
} from "./client";

export const getClassAssignmentQueryOptions = <
  T extends EnrichedTeacherClassAssignmentWithContentAssignees
>({
  classId,
  assignmentId,
  enabled = true
}: {
  classId: string;
  assignmentId: string;
  enabled?: boolean;
}): QueryOptions<T | undefined> => ({
  queryFn: () => fetchClassAssignment<T>({ classId, assignmentId }),
  queryKey: ["assignments", "class-assignment", classId, assignmentId],
  enabled
});

export const getAssignmentQueryOptions = (
  assignmentId: string
): QueryOptions<Assignment | undefined> => ({
  queryFn: () => fetchAssignment(assignmentId),
  queryKey: ["assignments", "assignment", assignmentId]
});

export type ClassAssignmentsFetchType = "upcoming" | "all";

export const getClassAssignmentsQueryOptions = ({
  classId,
  dueDateFrom,
  fetchType = "all",
  limit
}: {
  classId: string;
  limit: number;
  dueDateFrom?: string;
  fetchType?: ClassAssignmentsFetchType;
}): QueryOptions<TeacherClassAssignment[] | undefined> => ({
  queryFn: () => fetchClassAssignments({ classId, date: dueDateFrom, limit }),
  queryKey: ["assignments", "class-assignments", classId, fetchType, { limit }]
});

export const getClassAssignmentExamQuestionAnswersQueryOptions = ({
  classId,
  assignmentId
}: {
  classId: string;
  assignmentId: string;
}): QueryOptions<FetchExamQuestionAnswersResponse | undefined> => ({
  queryFn: () => fetchAssignmentExamQuestionAnswers({ classId, assignmentId }),
  queryKey: ["assignments", "exam-questions-answers", classId, assignmentId]
});

export const getAllTeacherClassAssignmentsQueryOptions = <
  T extends {
    from?: string;
    to?: string;
    schoolId?: string;
    withAssignees?: boolean;
  }
>({
  from,
  to,
  schoolId,
  withAssignees,
  enabled = true
}: T & { enabled?: boolean }) => {
  return queryOptions({
    queryFn: async function () {
      const allAssignments = await fetchAllTeacherClassAssignments({
        from,
        to,
        schoolId,
        withAssignees
      });
      return allAssignments.sort((a, b) =>
        a.dueDate.localeCompare(b.dueDate)
      ) as FetchTeacherAssignments<T["withAssignees"]>["items"];
    },
    queryKey: [
      "assignments",
      "all-teacher-class-assignments",
      ...[removeUndefinedEntries({ from, to, schoolId, withAssignees })]
    ],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 30,
    enabled
  });
};

export const getTeacherAssignmentsQueryOptions = ({
  from,
  to
}: {
  from?: string;
  to?: string;
}): QueryOptions<Assignment[]> => ({
  queryFn: () => fetchTeacherAssignments(from, to),
  queryKey: [
    "assignments",
    "teacher-assignments",
    ...(!!from || !!to ? [{ from, to }] : [])
  ],
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30
});

export const getTeacherQuizQueryOptions = (
  quizId?: string
): QueryOptions<TeacherQuizI | undefined> => ({
  queryFn: () => fetchTeacherQuiz(quizId),
  queryKey: ["user-quiz", "quiz", quizId],
  enabled: !!quizId,
  staleTime: Infinity,
  refetchOnWindowFocus: "always"
});

export type QueryTeacherQuizzesArgs = Partial<
  Pick<QuizOverviewI, "tags" | "published">
> & {
  schoolId?: string;
  limit?: number;
  lastKey?: string;
  status?: QuizOverviewI["status"] | null;
  archived?: QuizOverviewI["archived"] | null;
  enabled?: boolean;
};

export type QuerySchoolQuizzesArgs = Partial<Pick<QuizOverviewI, "tags">> & {
  limit?: number;
  lastKey?: string;
  status?: QuizOverviewI["status"] | null;
  archived?: QuizOverviewI["archived"] | null;
  published?: QuizOverviewI["published"] | null;
};

export const QUERY_TEACHER_QUIZZES_KEY = ["user-quiz", "teacher-quizzes"];

export const queryTeacherQuizzes = (
  args: QueryTeacherQuizzesArgs
): QueryOptions<FetchTeacherQuizOverviewsResponse> => ({
  queryFn: () => queryTeacherQuizOverviews(args),
  queryKey: [...QUERY_TEACHER_QUIZZES_KEY, args] as QueryKey,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30
});

export const infiniteQueryTeacherQuizzes = (
  args: QueryTeacherQuizzesArgs
): UndefinedInitialDataInfiniteOptions<
  FetchTeacherQuizOverviewsResponse,
  DefaultError,
  InfiniteData<FetchTeacherQuizOverviewsResponse>,
  QueryKey,
  string | undefined
> => ({
  queryFn: ({ pageParam: lastKey }) =>
    queryTeacherQuizOverviews({
      ...args,
      lastKey
    }),
  queryKey: [...QUERY_TEACHER_QUIZZES_KEY, args] as QueryKey,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30,
  getNextPageParam: lastPage => lastPage.lastKey,
  initialPageParam: undefined
});

export const QUERY_SCHOOL_QUIZ_OVERVIEW_KEY = ["user-quiz", "school-quizzes"];

export const infiniteQuerySchoolQuizOverviews = (
  schoolId: string,
  args: QuerySchoolQuizzesArgs = {}
): UndefinedInitialDataInfiniteOptions<
  FetchSchoolQuizOverviewsResponse,
  DefaultError,
  InfiniteData<FetchSchoolQuizOverviewsResponse>,
  QueryKey,
  string | undefined
> => ({
  queryFn: ({ pageParam: lastKey }) =>
    querySchoolTeacherQuizOverviews(schoolId, {
      ...args,
      lastKey
    }),
  queryKey: [
    ...QUERY_SCHOOL_QUIZ_OVERVIEW_KEY,
    schoolId,
    { ...args, schoolId }
  ] as QueryKey,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30,
  getNextPageParam: lastPage => lastPage.lastKey,
  initialPageParam: undefined
});

export const querySchoolQuizOverviews = (
  schoolId: string,
  { tags, published = null, status, limit }: QuerySchoolQuizzesArgs = {}
) => ({
  queryFn: () =>
    querySchoolTeacherQuizOverviews(schoolId, {
      tags,
      published,
      status,
      limit
    }),
  queryKey: [
    ...QUERY_SCHOOL_QUIZ_OVERVIEW_KEY,
    schoolId,
    { tags, published, status, limit }
  ] as QueryKey,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30
});

export const getActiveUsersBySchoolQueryOptions = ({
  from,
  to,
  schoolId
}: {
  from: string;
  to: string;
  schoolId?: string;
}): QueryOptions<ActiveUsers> => ({
  queryFn: () =>
    fetchActiveUsersBySchool({ from, to, schoolId: schoolId ?? "" }),
  queryKey: [
    "school-analytics",
    "fetch-active-users",
    [{ from, to, schoolId }]
  ],
  retryOnMount: false,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30,
  enabled: !!schoolId
});

export const getSchoolConnectionInfoQueryOptions = ({
  schoolId,
  startDate
}: {
  schoolId: string;
  startDate: string;
}): QueryOptions<SchoolParentConnectionsInfoResponse> => ({
  queryFn: () => fetchSchoolConnectionInfo(schoolId, startDate),
  queryKey: [
    "schools-service",
    "fetch-school-connection-info",
    [{ schoolId, startDate }]
  ],
  retryOnMount: false,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30
});

export const getStudentQuizQueryOptions = (
  quizId: string
): SuspenseQueryOptions<TeacherQuizI> => ({
  queryFn: () => fetchStudentQuiz(quizId),
  queryKey: ["user-quiz", "student-quiz", quizId],
  retryOnMount: false,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 30
});

export function getQuizTagsQueryOptions({
  region,
  includeHidden = false
}: {
  region?: string;
  includeHidden?: boolean;
} = {}): UseSuspenseQueryOptions<FetchAllTagsResponse> {
  return getTagsQueryOptions({
    region,
    includeHidden,
    includeNonCourseFilters: true
  });
}

export const getUserSectionStatsQueryOptions = (
  config: UserSectionStatsGraphQlArgs,
  classId?: string
) => ({
  queryFn: () => fetchUserSectionStats(config),
  queryKey: ["stats-service", "user-section-stats", config.courseId, classId],
  retryOnMount: false,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: Infinity,
  enabled:
    !!config.courseId &&
    !!config.sectionIds?.length &&
    !!config.userIds?.length &&
    !!classId
});

export const getClassesSectionStatsQueryOptions = ({
  classIds,
  schoolId,
  sectionIds,
  courseId,
  bestScoreThreshold
}: {
  classIds: string[];
  schoolId?: string;
  sectionIds: string[];
  courseId: string;
  bestScoreThreshold: number;
}) => ({
  queryFn: () =>
    fetchClassesSectionStats({
      classIds,
      schoolId: schoolId ?? "",
      sectionIds,
      courseId,
      bestScoreThreshold
    }),
  queryKey: [
    "class-service",
    "classes-section-stats",
    classIds,
    sectionIds,
    schoolId,
    courseId,
    bestScoreThreshold
  ],
  retryOnMount: false,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: Infinity,
  enabled: !!classIds.length && !!sectionIds.length && !!courseId
});

export const getEnrichedAggregatedUserSectionStatsQueryOptions = ({
  sectionId,
  userIds,
  courseId,
  schoolId,
  enabled = true
}: {
  sectionId: string;
  userIds: string[];
  courseId: string;
  schoolId?: string;
  enabled?: boolean;
}) => ({
  queryFn: () =>
    fetchEnrichedAggregatedUserSectionStats({
      sectionId,
      userIds,
      schoolId: schoolId ?? "",
      courseId
    }),
  queryKey: [
    "stats-service",
    "enriched-aggregated-user-section-stats",
    sectionId,
    courseId,
    schoolId,
    userIds
  ],
  retryOnMount: false,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 5 * 60 * 1000,
  enabled: !!sectionId && !!userIds.length && !!courseId && enabled
});

export const getFullSchoolClassesTeacherQueryOptions = (
  schoolId: string | undefined,
  opts: Pick<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchFullSchoolClassesTeacher>>,
      unknown,
      Awaited<ReturnType<typeof fetchFullSchoolClassesTeacher>>
    >,
    "enabled" | "select"
  > = {}
): UseQueryOptions<
  Awaited<ReturnType<typeof fetchFullSchoolClassesTeacher>>,
  unknown,
  Awaited<ReturnType<typeof fetchFullSchoolClassesTeacher>>
> => ({
  queryKey: ["school-classes", schoolId],
  queryFn: () => fetchFullSchoolClassesTeacher(schoolId),
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 5,
  ...opts
});

export const getSchoolClassEntitiesTeacherQueryOptions = (
  schoolId: string | undefined,
  opts: Pick<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchSchoolClassEntitiesTeacher>>,
      unknown,
      Awaited<ReturnType<typeof fetchSchoolClassEntitiesTeacher>>
    >,
    "enabled" | "select"
  > = {}
): UseQueryOptions<
  Awaited<ReturnType<typeof fetchSchoolClassEntitiesTeacher>>,
  unknown,
  Awaited<ReturnType<typeof fetchSchoolClassEntitiesTeacher>>
> => ({
  queryKey: ["school-classes", schoolId],
  queryFn: () => fetchSchoolClassEntitiesTeacher(schoolId),
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 1000 * 60 * 5,
  ...opts
});

export const getEQEnrichedCourseSectionsQueryOptions = (
  {
    hardQuestionsEnabled = true,
    ...args
  }: {
    hardQuestionsEnabled?: boolean;
    courseId?: string | null;
    sectionId?: string;
    assignmentId?: string | null;
  },
  {
    select,
    enabled
  }: Pick<
    UseQueryOptions<
      EQEnrichedCourseSections[],
      unknown,
      EQEnrichedCourseSections[] | undefined
    >,
    "enabled" | "select"
  > = {},
  staticGenerationSeed: string = uuid()
) => ({
  queryKey: ["short-answer-question-enriched-course-sections", args],
  queryFn: () => queryEnrichedCourseSections(args),
  select: (data: EQEnrichedCourseSections[]) => {
    const defaultData = (
      hardQuestionsEnabled
        ? data
        : data.filter(it => it.difficulty !== QuestionDifficulty.HARD)
    ).map(
      enrichEQWithGenerationSeed(args.assignmentId || staticGenerationSeed)
    );

    return select ? select(defaultData) : defaultData;
  },
  enabled: enabled ?? !!args.courseId,
  staleTime: Infinity,
  retry: false
});
